/**
 * s3-signer — S3 에 직접 PUT 하기 위한 SigV4 서명.
 *
 * 왜 직접 만드나: `@lemoncloud/lemon-web-core` 도 `sigV4Client` 를 export 하지만, 그
 * `signRequest` 는 바디를 문자열로 다루고(객체면 `JSON.stringify`) **그 문자열로 페이로드
 * 해시를 만든다**. 우리가 올리는 것은 WAV 바이트라 그 경로로는 서명이 맞지 않는다. AWS SDK
 * 를 번들에 넣기에는 PUT 한 건에 필요한 서명이 이 파일 하나 분량이다.
 *
 * 알고리즘은 AWS Signature Version 4 문서 그대로다. 검증은 AWS 가 공개한 테스트 벡터
 * (`get-vanilla`)로 한다 — 서명은 눈으로 못 읽는 코드라서, 우리 구현으로 기대값을 다시
 * 계산하는 테스트는 아무것도 보호하지 못한다.
 */

import { AWS_REGION, MEDIA_BUCKET, MEDIA_PREFIX } from '@muzly/shared';

import { getWebTransport } from './transport';

const ALGORITHM = 'AWS4-HMAC-SHA256';
const TERMINATOR = 'aws4_request';

export interface SigV4Credentials {
  accessKeyId: string;
  /** lemon-core 는 이 값을 `SecretKey` 라는 이름으로 준다 — 여기 들어오기 전에 바꾼다. */
  secretAccessKey: string;
  sessionToken?: string;
}

export interface SigV4Input {
  method: string;
  host: string;
  /** 인코딩 **전** 경로. 퍼센트 인코딩은 이 모듈이 한다. */
  path: string;
  query?: Record<string, string>;
  /** 추가로 서명할 헤더. `host`·`x-amz-date`·`x-amz-security-token` 은 여기서 채운다. */
  headers?: Record<string, string>;
  /** 보낼 바이트의 SHA-256(hex). S3 는 이 값을 `x-amz-content-sha256` 로도 요구한다. */
  payloadHashHex: string;
  region: string;
  service: string;
  credentials: SigV4Credentials;
  /** 테스트가 시각을 고정할 수 있도록 주입받는다. */
  date?: Date;
}

const encoder = new TextEncoder();

const toHex = (buffer: ArrayBuffer): string =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

const sha256Hex = async (data: Uint8Array): Promise<string> =>
  toHex(await crypto.subtle.digest('SHA-256', data as BufferSource));

const hmac = async (key: Uint8Array, message: string): Promise<ArrayBuffer> => {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return crypto.subtle.sign('HMAC', cryptoKey, encoder.encode(message));
};

/**
 * canonical URI 의 인코딩 규칙: 슬래시는 구분자로 남기고, 나머지는 `A-Za-z0-9-_.~` 만
 * 날것으로 두고 전부 퍼센트 인코딩한다. `encodeURIComponent` 가 남기는
 * `!'()*` 를 마저 인코딩해야 AWS 와 같은 문자열이 된다.
 *
 * 우리 key 에는 콜론이 들어간다(`identityId` = `ap-northeast-2:<uuid>`) — 그것도 여기서
 * `%3A` 가 된다. **보내는 URL 도 반드시 이 결과를 써야 한다**: 서명한 경로와 요청한 경로가
 * 한 글자라도 다르면 S3 는 403 을 준다.
 */
const encodeSegment = (value: string): string =>
  encodeURIComponent(value).replace(
    /[!'()*]/g,
    (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`,
  );

const encodePath = (path: string): string =>
  path.split('/').map(encodeSegment).join('/');

/** 경로와 **같은** 인코더를 쓴다 — 규칙이 갈리면 그 차이가 그대로 403이다. */
const canonicalQuery = (query: Record<string, string>): string =>
  Object.keys(query)
    .sort()
    .map((key) => `${encodeSegment(key)}=${encodeSegment(query[key])}`)
    .join('&');

/**
 * 서명하고, **보낼 헤더 전부**를 돌려준다(`Authorization` 포함). 여기서 뺀 헤더를 나중에
 * 더하면 서명이 어긋난다.
 *
 * 반환에는 `host` 도 들어 있다. 브라우저 `fetch` 는 그것을 forbidden header 로 조용히
 * 버리지만(무해 — 실제 Host 는 URL 이 정한다) 서명 대상에는 반드시 있어야 해서 남긴다.
 */
export const signSigV4 = async (
  input: SigV4Input,
): Promise<Record<string, string>> => {
  const date = input.date ?? new Date();
  const amzDate = date.toISOString().replace(/[:-]|\.\d{3}/g, '');
  const dateStamp = amzDate.slice(0, 8);

  // 키를 먼저 소문자로 모은다. `Host` 와 `host` 가 함께 들어오면 서명 대상 목록에 같은
  // 이름이 두 번 실려 canonical string 이 어긋난다.
  const headers: Record<string, string> = {
    ...Object.fromEntries(
      Object.entries(input.headers ?? {}).map(([name, value]) => [
        name.toLowerCase(),
        value,
      ]),
    ),
    host: input.host,
    'x-amz-date': amzDate,
  };
  if (input.credentials.sessionToken) {
    headers['x-amz-security-token'] = input.credentials.sessionToken;
  }

  const signedNames = Object.keys(headers).sort();
  // AWS 는 헤더 값의 양끝 공백을 자르고 **연속 공백을 하나로 줄인** 것에 서명한다.
  // 파일명 메타데이터처럼 공백이 둘 이상 들어갈 수 있는 값에서 이 축약이 없으면 어긋난다.
  const normalized = Object.fromEntries(
    Object.entries(headers).map(([name, value]) => [
      name,
      value.trim().replace(/\s+/g, ' '),
    ]),
  );
  const canonicalHeaders = signedNames
    .map((name) => `${name}:${normalized[name]}\n`)
    .join('');
  const signedHeaders = signedNames.join(';');

  const canonicalRequest = [
    input.method.toUpperCase(),
    encodePath(input.path),
    canonicalQuery(input.query ?? {}),
    canonicalHeaders,
    signedHeaders,
    input.payloadHashHex,
  ].join('\n');

  const scope = `${dateStamp}/${input.region}/${input.service}/${TERMINATOR}`;
  const stringToSign = [
    ALGORITHM,
    amzDate,
    scope,
    await sha256Hex(encoder.encode(canonicalRequest)),
  ].join('\n');

  // 서명 키는 날짜 → 리전 → 서비스 → 종결자 순으로 네 번 감는다(SigV4 문서 그대로).
  const dateKey = new Uint8Array(
    await hmac(
      encoder.encode(`AWS4${input.credentials.secretAccessKey}`),
      dateStamp,
    ),
  );
  const regionKey = new Uint8Array(await hmac(dateKey, input.region));
  const serviceKey = new Uint8Array(await hmac(regionKey, input.service));
  const signingKey = new Uint8Array(await hmac(serviceKey, TERMINATOR));
  const signature = toHex(await hmac(signingKey, stringToSign));

  return {
    ...headers,
    Authorization:
      `${ALGORITHM} Credential=${input.credentials.accessKeyId}/${scope}, ` +
      `SignedHeaders=${signedHeaders}, Signature=${signature}`,
  };
};

export interface SignS3PutInput {
  /** 인코딩 전 오브젝트 키. 프리픽스는 호출부가 만든다(`medias/<stage>/<identityId>/…`). */
  key: string;
  contentType: string;
  body: Uint8Array;
  /** 테스트가 서명을 고정하기 위한 주입구. 프로덕션 호출부는 넘기지 않는다. */
  overrides?: {
    region?: string;
    bucket?: string;
    credentials?: SigV4Credentials;
    date?: Date;
  };
}

export interface SignedS3Put {
  url: string;
  headers: Record<string, string>;
  /**
   * 실제로 서명한 버킷. 호출부가 `MEDIA_BUCKET` 을 따로 읽으면 `overrides` 를 쓰는 순간
   * PUT 대상과 등록 레코드의 `bucket` 이 갈리는데, 서버는 파일을 열지 않으므로 그 어긋남이
   * 조용히 남는다. 읽는 자리를 여기 하나로 둔다.
   */
  bucket: string;
}

/**
 * 이 앱이 S3 에 올릴 때 쓰는 신원. **크리덴셜은 이 모듈 밖으로 나가지 않는다** — 업로드
 * 호출부는 `identityId` 만 알면 되고, 그것은 비밀이 아니라 키 프리픽스다.
 *
 * `identityId` 가 왜 필요한가: role 정책이 `medias/<stage>/${cognito-identity.amazonaws.com:sub}/*`
 * 로 프리픽스를 강제한다. 다른 모양의 키는 403 이다.
 */
export const getUploadIdentity = async (): Promise<string | null> => {
  if (!isMediaUploadEnabled()) {
    return null;
  }
  try {
    // `getSavedToken()` 을 쓰는 이유: 이 리더에는 스네이크(`identity_id`)로 못 찾으면
    // 카멜(`identityId`)로 한 번 더 보는 폴백이 들어 있다. 스토리지를 직접 읽으면 그
    // 폴백이 빠져, 다른 경로로 저장된 값을 조용히 못 읽는다.
    const saved = await getWebTransport().getSavedToken();
    const entry = Object.entries(saved ?? {}).find(([key]) =>
      key.endsWith('.identity_id'),
    );
    return entry?.[1] ? String(entry[1]) : null;
  } catch {
    return null;
  }
};

/**
 * 버킷·프리픽스 중 하나라도 비면 업로드 기능 자체가 없다 — 파일을 읽지도, 서명하지도
 * 않는다. 둘 다 보는 이유: 프리픽스가 없으면 만들 수 있는 키가 IAM 조건에 안 맞아 403 인데,
 * 그 403 은 서명이 틀린 것과 구분되지 않는다.
 */
export const isMediaUploadEnabled = (): boolean =>
  MEDIA_BUCKET !== '' && MEDIA_PREFIX !== '';

/**
 * S3 PUT 한 건을 서명한다. 크리덴셜은 `lemon-web-core` 가 이미 캐시하고 만료 시 갱신까지
 * 하므로(`AWSWebCore.getCredentials()`) 여기서 꺼내 쓴다 — 사본을 따로 만들지 않는다.
 * 크리덴셜이 없으면(세션 전, 갱신 실패) `null` 이고, 호출부는 업로드를 건너뛴다.
 *
 * 가상 호스트 주소(`<bucket>.s3.<region>.amazonaws.com`)를 쓰므로 경로에는 키만 남는다.
 * `x-amz-acl` 은 보내지 않는다 — 버킷의 `BlockPublicAcls` 가 켜져 있어 거부되고, 공개
 * 여부는 버킷 정책이 정한다.
 */
export const signS3Put = async (
  input: SignS3PutInput,
): Promise<SignedS3Put | null> => {
  const bucket = input.overrides?.bucket ?? MEDIA_BUCKET;
  if (bucket === '') {
    return null;
  }
  const credentials = input.overrides?.credentials ?? (await readCredentials());
  if (credentials === null) {
    return null;
  }

  const region = input.overrides?.region ?? AWS_REGION;
  const host = `${bucket}.s3.${region}.amazonaws.com`;
  const path = `/${input.key}`;
  const payloadHashHex = await sha256Hex(input.body);

  const headers = await signSigV4({
    method: 'PUT',
    host,
    path,
    headers: {
      'content-type': input.contentType,
      'x-amz-content-sha256': payloadHashHex,
    },
    payloadHashHex,
    region,
    service: 's3',
    credentials,
    date: input.overrides?.date,
  });

  return { url: `https://${host}${encodePath(path)}`, headers, bucket };
};

/**
 * lemon-web-core 의 크리덴셜을 우리 이름으로 옮긴다. 그쪽은 `SecretKey`, AWS 서명은
 * `secretAccessKey` — 이름이 갈리는 자리가 여기 하나여야 한다.
 */
const readCredentials = async (): Promise<SigV4Credentials | null> => {
  try {
    const credential = await getWebTransport().getCredentials();
    const accessKeyId = credential?.AccessKeyId;
    const secretAccessKey = credential?.SecretKey;
    if (!accessKeyId || !secretAccessKey) {
      return null;
    }
    return {
      accessKeyId,
      secretAccessKey,
      sessionToken: credential?.SessionToken || undefined,
    };
  } catch {
    return null;
  }
};
